<template>
    <article class="article-content">
        <section class="content-section">
            <h2>{{ sections?.intro?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.intro?.content)"
                :key="'intro-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.entity_definitions?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.entity_definitions?.content)"
                :key="'entity-definitions-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/entities/create_user_entity_definition-400w.webp   400w,
                            /images/blog/entities/create_user_entity_definition-800w.webp   800w,
                            /images/blog/entities/create_user_entity_definition-1200w.webp 1200w,
                            /images/blog/entities/create_user_entity_definition-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/entities/create_user_entity_definition.png"
                        alt="Creating a new entity definition for Users"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.entity_definition }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.what_are_entities?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.what_are_entities?.content)"
                :key="'what-are-entities-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/entities/user_data-400w.webp   400w,
                            /images/blog/entities/user_data-800w.webp   800w,
                            /images/blog/entities/user_data-1200w.webp 1200w,
                            /images/blog/entities/user_data-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/entities/user_data.png"
                        alt="User entity with populated data fields"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.user_data }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.field_types?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.field_types?.content)"
                :key="'field-types-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.field_options?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.field_options?.content)"
                :key="'field-options-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/entities/user_definition_fields-400w.webp   400w,
                            /images/blog/entities/user_definition_fields-800w.webp   800w,
                            /images/blog/entities/user_definition_fields-1200w.webp 1200w,
                            /images/blog/entities/user_definition_fields-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/entities/user_definition_fields.png"
                        alt="Entity definition fields with various types and constraints"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.field_config }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.definition_options?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.definition_options?.content)"
                :key="'definition-options-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.creating_entities?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.creating_entities?.content)"
                :key="'creating-entities-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/entities/user_creation-400w.webp   400w,
                            /images/blog/entities/user_creation-800w.webp   800w,
                            /images/blog/entities/user_creation-1200w.webp 1200w,
                            /images/blog/entities/user_creation-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/entities/user_creation.png"
                        alt="Creating a new entity instance manually"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.entity_creation }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.data_entry_points?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.data_entry_points?.content)"
                :key="'data-entry-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.public_api?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.public_api?.content)"
                :key="'public-api-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section teaser-section">
            <h2>{{ sections?.whats_next?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.whats_next?.content)"
                :key="'whats-next-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <div class="coming-soon-badge">
                <SmartIcon
                    icon="clock"
                    :size="18"
                />
                {{ t('blog.coming_soon') }}: Entity & Definition Versioning
            </div>
        </section>
    </article>
</template>

<script setup lang="ts">
    import {
        useBlogFormatter,
        type PostSections,
        type PostCaptions,
    } from '@/composables/useBlogFormatter'
    import { useTranslations } from '@/composables/useTranslations'
    import SmartIcon from '@/components/common/SmartIcon.vue'

    defineProps<{
        sections?: PostSections
        captions?: PostCaptions
    }>()

    const { t } = useTranslations()
    const { splitParagraphs, formatContent } = useBlogFormatter()
</script>
